import { useCallback, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { haptic } from "@/lib/telegram";

type Ripple = { id: number; x: number; y: number };

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: "gradient" | "glass" | "ghost";
  size?: "sm" | "md" | "lg";
};

const sizes = {
  sm: "h-10 px-4 text-sm",
  md: "h-12 px-5 text-[15px]",
  lg: "h-16 px-8 text-lg",
};

export function RippleButton({
  children,
  className,
  variant = "gradient",
  size = "md",
  onPointerDown,
  ...props
}: Props) {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const counter = useRef(0);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const id = counter.current++;
      setRipples((prev) => [
        ...prev,
        { id, x: event.clientX - rect.left, y: event.clientY - rect.top },
      ]);
      window.setTimeout(() => setRipples((prev) => prev.filter((r) => r.id !== id)), 650);
      haptic("light");
      onPointerDown?.(event);
    },
    [onPointerDown],
  );

  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", stiffness: 480, damping: 26 }}
      onPointerDown={handlePointerDown}
      className={cn(
        "relative isolate overflow-hidden rounded-full font-semibold tracking-tight",
        "transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50",
        sizes[size],
        variant === "gradient" &&
          "text-primary-foreground [background-image:var(--gradient-brand)] [box-shadow:var(--shadow-glow)]",
        variant === "glass" && "glass text-foreground",
        variant === "ghost" && "bg-secondary/60 text-foreground",
        className,
      )}
      {...(props as React.ComponentProps<typeof motion.button>)}
    >
      <span className="relative z-10 inline-flex items-center justify-center gap-2">{children}</span>
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          aria-hidden
          className="pointer-events-none absolute z-0 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40"
          style={{
            left: ripple.x,
            top: ripple.y,
            animation: "ripple-out 620ms ease-out forwards",
          }}
        />
      ))}
    </motion.button>
  );
}